/**
 * Symbols for the TradingView ticker tape (`components/market-data/TradingViewTickerTape.vue`).
 *
 * TradingView doesn't license the real index feeds (SP:SPX, NASDAQ:IXIC and NDX, TVC:RUT, TVC:DXY,
 * NYMEX:CL1!) to its free embeddable widgets: they draw an error icon where the quote should be. The
 * market symbols below are trackers that do render.
 */

import routeManager from '@/router/route-manager';

export type TickerTapeSymbol = {
    // `EXCHANGE:SYMBOL`, or a bare ticker that TradingView resolves itself
    proName: string;
    // Shown in place of the symbol
    title?: string;
};

// The logged-out homepage shows these on their own; the dashboard puts them ahead of the member's tickers
export const MARKET_SYMBOLS: readonly TickerTapeSymbol[] = [
    { proName: 'FOREXCOM:SPXUSD', title: 'S&P 500' },
    { proName: 'FOREXCOM:NSXUSD', title: 'Nasdaq' },
    { proName: 'FOREXCOM:US2000', title: 'Russell 2000' },
    // Neither FOREX.com's nor TVC's dollar index renders in the widget; Capital.com's does
    { proName: 'CAPITALCOM:DXY', title: 'DXY' },
    { proName: 'TVC:USOIL', title: 'Crude Oil' },
];

// The tape scrolls at a fixed speed, so every symbol lengthens the loop. It carries at most this many of
// the member's tickers, and a member with more than that gets the whole tape: no market symbols.
export const MAX_MEMBER_TICKERS = 25;

// TradingView writes class shares with a dot. Handed `BRK-B` or `BRK/B` it quietly quotes some other
// instrument, so rewrite the separators other data sources use.
const CLASS_SHARE_TICKER = /^([A-Z]+)[-/]([A-Z])$/;

export function toTradingViewTicker( ticker: string ): string {
    return ticker.trim().toUpperCase().replace( CLASS_SHARE_TICKER, '$1.$2' );
}

/**
 * The market symbols followed by the member's tickers (normalized, de-duplicated in first-seen order,
 * blanks dropped), capped at `maxTickers`. A member with more tickers than that gets the tape to
 * themselves, without the market symbols.
 */
export function buildTickerTapeSymbols(
    marketSymbols: readonly TickerTapeSymbol[],
    tickers: readonly ( string | null | undefined )[],
    maxTickers: number = MAX_MEMBER_TICKERS
): TickerTapeSymbol[] {
    const memberTickers = new Set< string >();

    for ( const ticker of tickers ) {
        const proName = toTradingViewTicker( ticker ?? '' );
        if ( proName ) {
            memberTickers.add( proName );
        }
    }

    const leading = memberTickers.size > maxTickers ? [] : marketSymbols;
    const members = [ ...memberTickers ].slice( 0, maxTickers ).map( proName => ( { proName } ) );

    return [ ...leading, ...members ];
}

// An equity ticker as it can appear on the tape: letters and digits, with `.` or `-` for class shares and
// preferreds (BRK.B, ABR-PD). Nothing that could add a path segment, query or fragment to the route.
const CLICKABLE_TICKER = /^[A-Za-z0-9][A-Za-z0-9.-]{0,14}$/;

/**
 * Where a click on the tape lands. TradingView links each symbol to `/securities?tvwidgetsymbol=<proName>`
 * (the tape's largeChartUrl): the member's tickers go to their security page, and the market symbols,
 * which have no page here, go home. That URL can be crafted by anyone, so anything that is not a
 * well-formed ticker goes home too.
 */
export function tickerTapeClickPath( tvWidgetSymbol: unknown ): string {
    const value = Array.isArray( tvWidgetSymbol ) ? tvWidgetSymbol[ 0 ] : tvWidgetSymbol;
    const proName = typeof value === 'string' ? value.trim() : '';
    // The member's tickers go out bare; tolerate an exchange prefix (NASDAQ:PLTR) all the same
    const ticker = proName.slice( proName.lastIndexOf( ':' ) + 1 );

    const isMarketSymbol = MARKET_SYMBOLS.some( symbol => symbol.proName === proName );
    return CLICKABLE_TICKER.test( ticker ) && !isMarketSymbol ? routeManager.buildSecurityPath( ticker ) : routeManager.homePath();
}
